import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section } from '../components/shared/Section';
import { SectionHeading } from '../components/shared/SectionHeading';
import { GlassCard } from '../components/shared/GlassCard';
import { Particles } from '../components/shared/Particles';
import { SEO } from '../components/shared/SEO';
import { SERVICES, CONTACT_INFO } from '../lib/constants';

// ─── JSON-LD: Service List ────────────────────────────────────────────────────
const SERVICES_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Hydrox Studios Services',
  description:
    'Professional audio and video post-production services offered by Hydrox Studios in Guwahati, Assam.',
  url: 'https://hydroxstudios.com/services',
  itemListElement: SERVICES.map((service, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Service',
      '@id': `https://hydroxstudios.com/services#${service.id}`,
      name: service.title,
      description: service.fullDescription ?? service.shortDescription,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Hydrox Studios',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Guwahati',
          addressRegion: 'Assam',
          addressCountry: 'IN',
        },
      },
      areaServed: 'IN',
    },
  })),
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://hydroxstudios.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Services',
      item: 'https://hydroxstudios.com/services',
    },
  ],
};

// ─── Breadcrumb UI ────────────────────────────────────────────────────────────
const Breadcrumb = () => (
  <nav aria-label="Breadcrumb" className="absolute top-24 left-6 z-20 hidden sm:block">
    <ol className="flex items-center gap-2 text-xs font-manrope text-white/40">
      <li>
        <Link to="/" className="hover:text-white/70 transition-colors">
          Home
        </Link>
      </li>
      <li aria-hidden="true">/</li>
      <li>
        <span className="text-primary" aria-current="page">
          Services
        </span>
      </li>
    </ol>
  </nav>
);

// ─── Quick-jump Nav ───────────────────────────────────────────────────────────
const ServiceNav = () => (
  <nav
    aria-label="Jump to service section"
    className="sticky top-16 z-30 bg-black/60 backdrop-blur-md border-b border-white/10 py-3"
  >
    <ul className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap px-4">
      {SERVICES.map((service) => (
        <li key={service.id}>
          <a
            href={`#${service.id}`}
            className="text-xs sm:text-sm font-manrope text-white/60 hover:text-primary transition-colors px-3 py-1 rounded-full hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {service.title}
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

// ─── Page Hero ────────────────────────────────────────────────────────────────
const PageHero = () => {
  return (
    <section
      className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20"
      aria-labelledby="services-page-heading"
    >
      <Breadcrumb />

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" aria-hidden="true" />
      <Particles count={15} />

      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-sm uppercase tracking-[0.3em] text-primary font-manrope mb-6"
        >
          Hydrox Studios · Guwahati, Assam
        </motion.p>

        {/* H1 targets "services" page keyword */}
        <motion.h1
          id="services-page-heading"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="font-syne text-4xl sm:text-5xl md:text-6xl font-bold text-white tracking-tight mb-6"
        >
          Audio &amp; Video Post-Production Services
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="font-manrope text-base md:text-lg text-muted-foreground max-w-2xl mx-auto"
        >
          End-to-end music video production, audio mixing &amp; mastering, brand films and
          cinematic post-production — serving artists and brands across Assam and India.
        </motion.p>
      </div>
    </section>
  );
};

// ─── Service Detail Card ──────────────────────────────────────────────────────
const ServiceDetail = ({ service, index }) => {
  const Icon = service.icon;
  const isEven = index % 2 === 0;
  const isMusicVideo = service.title === 'Music Videos';

  return (
    <motion.article
      id={service.id}
      className="scroll-mt-32"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay: 0.05 }}
      aria-labelledby={`service-heading-${service.id}`}
    >
      <GlassCard className="p-8 md:p-12" hover={false}>
        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 ${
            !isEven ? 'lg:flex lg:flex-row-reverse' : ''
          }`}
        >
          {/* Content */}
          <div className="flex flex-col justify-center">
            <div
              className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6"
              aria-hidden="true"
            >
              <Icon size={28} className="text-primary" />
            </div>

            {/* H2 per service – strong on-page signal */}
            <h2
              id={`service-heading-${service.id}`}
              className="font-syne text-3xl md:text-4xl font-bold text-white mb-4"
            >
              {service.title}
              {/* Append location context for Music Videos – primary keyword page */}
              {isMusicVideo && (
                <span className="block text-xl md:text-2xl text-primary/80 mt-1 font-medium">
                  in Guwahati, Assam
                </span>
              )}
            </h2>

            <p className="text-muted-foreground font-manrope text-base leading-relaxed mb-8">
              {service.fullDescription}
            </p>

            {/* What's Included */}
            <div className="mb-8">
              <h3 className="font-syne text-lg font-semibold text-white mb-4">
                What's Included
              </h3>
              <ul className="space-y-3" aria-label={`What's included in ${service.title}`}>
                {service.included.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div
                      className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center mt-0.5"
                      aria-hidden="true"
                    >
                      <Check size={12} className="text-primary" />
                    </div>
                    <span className="text-muted-foreground font-manrope text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Who It's For + Timeline */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="font-syne text-lg font-semibold text-white mb-2">
                  Who It's For
                </h3>
                <p className="text-muted-foreground font-manrope text-sm">{service.whoItsFor}</p>
              </div>
              <div>
                <h3 className="font-syne text-lg font-semibold text-white mb-2">
                  Timeline
                </h3>
                <p className="text-muted-foreground font-manrope text-sm">{service.timeline}</p>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Link to="/contact">
                <Button
                  data-testid={`service-cta-${service.id}`}
                  className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope w-fit"
                  aria-label={`Get a quote for ${service.title}`}
                >
                  Get a Quote
                  <ArrowRight size={16} className="ml-2" aria-hidden="true" />
                </Button>
              </Link>

              {/* Music Videos gets a deep-link to its dedicated SEO landing page */}
              {isMusicVideo && (
                <Link to="/music-video-production-guwahati">
                  <Button
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 font-manrope w-fit"
                    aria-label="Learn more about music video production in Guwahati"
                  >
                    Learn More
                    <ArrowRight size={16} className="ml-2" aria-hidden="true" />
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Visual placeholder */}
          <div className="relative flex items-center">
            <div className="relative w-full">
              <div
                className="aspect-[4/3] rounded-lg overflow-hidden bg-secondary/50 flex items-center justify-center"
                role="img"
                aria-label={`${service.title} service visual`}
              >
                <Icon size={80} className="text-primary/20" aria-hidden="true" />
              </div>
              {/* Decorative border */}
              <div
                className="absolute -bottom-4 -right-4 w-32 h-32 border border-primary/20 rounded-lg -z-10"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </GlassCard>
    </motion.article>
  );
};

// ─── CTA Section ──────────────────────────────────────────────────────────────
const CTASection = () => {
  const whatsappNumber = CONTACT_INFO.whatsapp?.replace('+', '') ?? '';
  const whatsappHref = `https://wa.me/${whatsappNumber}?text=Hi%2C%20I'd%20like%20to%20discuss%20my%20project.`;

  return (
    <Section aria-labelledby="services-cta-heading">
      <div className="relative overflow-hidden rounded-2xl glass-heavy p-12 md:p-16 text-center">
        <div className="hero-glow -top-32 -left-32" aria-hidden="true" />
        <div className="hero-glow -bottom-32 -right-32" aria-hidden="true" />

        <div className="relative z-10">
          <h2
            id="services-cta-heading"
            className="font-syne text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Not Sure Which Service You Need?
          </h2>
          <p className="text-muted-foreground font-manrope text-base max-w-xl mx-auto mb-8">
            Let's discuss your project and we'll recommend the best approach for your
            goals and budget.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="services-page-cta-primary"
                size="lg"
                className="btn-glow bg-primary hover:bg-primary/90 text-white font-manrope px-8"
                aria-label="Contact Hydrox Studios for a project consultation"
              >
                Get a Quote
                <ArrowRight size={18} className="ml-2" aria-hidden="true" />
              </Button>
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with Hydrox Studios on WhatsApp"
            >
              <Button
                data-testid="services-page-cta-secondary"
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 font-manrope px-8"
              >
                WhatsApp Direct
              </Button>
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div data-testid="services-page">
      {/* ── SEO ── */}
      <SEO
        title="Audio & Video Post-Production Services – Hydrox Studios, Guwahati"
        description="Hydrox Studios offers professional music video production, audio mixing & mastering, brand films, and cinematic post-production in Guwahati, Assam. Serving artists and brands across India."
        path="/services"
        canonicalUrl="https://hydroxstudios.com/services"
        ogImage="https://hydroxstudios.com/og-services.jpg"
        ogType="website"
        keywords="audio post-production Guwahati, video editing Assam, music video production, audio mixing mastering, brand films Guwahati, Hydrox Studios services"
      />

      {/* ── JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(SERVICES_SCHEMA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(BREADCRUMB_SCHEMA) }}
      />

      {/* ── Layout ── */}
      <main id="main-content">
        <PageHero />
        <ServiceNav />

        <Section>
          {/* Semantic list of service articles */}
          <div className="space-y-16" role="list" aria-label="Hydrox Studios services">
            {SERVICES.map((service, index) => (
              <ServiceDetail key={service.id} service={service} index={index} />
            ))}
          </div>
        </Section>

        <CTASection />
      </main>
    </div>
  );
}